import { getBaseThemeName, getHazardColor, getHazardIcon } from "@/features/branding";
import { HazardPlusTotal, Year } from "@/features/pe_aldd";
import { AnaHazardId } from "@/store/services/facades/types";
import { PeaSummaryResponse } from "@/store/services/facades/types/pea";
import { Rcp } from "@/types/rcp";
import { CloseCircleFilled } from "@ant-design/icons";
import classNames from "classnames";
import clamp from "lodash/clamp";
import { ReactNode, useMemo } from "react";
import { filterResultsByMetric, formatYAxisTick, formatYAxisTickToPercent, MetricKey, MetricMaxima } from "./common/utils";
const ConditionalButton = ({
  isInteractive,
  onClick,
  className,
  children
}: {
  isInteractive: boolean;
  onClick?: () => void;
  className: string;
  children: ReactNode;
}) => {
  if (isInteractive) {
    return <button onClick={onClick} className={className}>
        {children}
      </button>;
  }
  return <div className={className} data-sentry-component="ConditionalButton" data-sentry-source-file="HazardSelectedYearBreakdown.tsx">{children}</div>;
};
type Props = {
  data: PeaSummaryResponse;
  rcp: Rcp;
  selectedYear: Year;
  hazard: HazardPlusTotal;
  setHazard: (hazard: AnaHazardId) => void;
  metricMax?: MetricMaxima;
  normalized?: boolean;
  metric: MetricKey;
};
export const HazardSelectedYearBreakdown = ({
  data,
  rcp,
  selectedYear,
  hazard,
  setHazard,
  metricMax,
  normalized,
  metric
}: Props) => {
  const isHkma = getBaseThemeName() === "hkma";
  const rows = useMemo(() => {
    return filterResultsByMetric({
      data,
      rcp,
      year: selectedYear,
      metric,
      includeTotal: false
    });
  }, [data, rcp, selectedYear, metric]);
  const totalForYear = useMemo(() => {
    if (!normalized) return undefined;
    return rows.filter(r => r.hazard !== "total").reduce((acc, r) => acc + r.value, 0);
  }, [rows, normalized]);

  // Determine domain max for the selected metric.
  // If parent provided a global max, use it; otherwise, derive from selected year.
  const domainMaxForMetric = useMemo(() => {
    let globalMax: number | undefined;

    // Only use the provided metricMax if metric is one of the supported metrics
    if (metricMax) {
      if (metric === "avg_damage_mtd") {
        globalMax = metricMax.avgDamageMtd;
      } else if (metric === "avg_failure_mtd") {
        globalMax = metricMax.avgFailureMtd;
      } else if (metric === "productivity_loss") {
        globalMax = metricMax.productivityLoss;
      } else if (metric === "ttip") {
        globalMax = metricMax.ttip;
      }
    }
    if (globalMax && globalMax > 0) {
      return globalMax;
    }
    const localMax = rows.reduce((max, r) => Math.max(max, r.value), 0);
    return localMax || 100;
  }, [rows, metric, metricMax, normalized]);
  const computeWidthPercent = (value: number): number => {
    if (normalized) {
      const total = totalForYear ?? 0;
      const percentOfTotal = total > 0 ? value / total * 100 : 0;
      return clamp(percentOfTotal, 0, 100);
    }
    const percentOfDomain = domainMaxForMetric > 0 ? value / domainMaxForMetric * 100 : 0;
    return clamp(percentOfDomain, 0, 100);
  };
  if (!rows.length) {
    return <div className="flex h-[540px] w-full items-center justify-center text-sm text-gray-400">No data</div>;
  }
  const handleHazardClick = (currentHazard: AnaHazardId) => {
    if (hazard === currentHazard) {
      setHazard("total" as AnaHazardId);
    } else {
      setHazard(currentHazard);
    }
  };
  return <div className="h-max w-full" style={{
    overflowY: "auto"
  }} data-sentry-component="HazardSelectedYearBreakdown" data-sentry-source-file="HazardSelectedYearBreakdown.tsx">
      <div className="grid h-full grid-cols-2 justify-start gap-2 @4xl/wrapper:grid-cols-1">
        {rows.sort((a, b) => b.value - a.value).map(row => {
        const color = getHazardColor(row.hazard as AnaHazardId);
        const widthPct = computeWidthPercent(row.value);
        const icon = getHazardIcon(row.hazard as any, {
          size: 20,
          style: {
            color
          }
        });
        const selectedHazard = hazard !== "total" && row.hazard === hazard;
        return <ConditionalButton key={row.hazard} isInteractive={!isHkma} onClick={() => handleHazardClick(row.hazard as AnaHazardId)} className={classNames(`relative overflow-hidden rounded-lg border border-gray-50/0 bg-gray-50 p-0.5 py-1 transition-all`, {
          "opacity-30 hover:opacity-100": hazard !== "total" && row.hazard !== hazard && !isHkma,
          "cursor-pointer hover:border-gray-300 active:scale-[0.99]": !isHkma
        })}>
                <div className="flex items-center justify-between">
                  <div className="flex min-w-0 items-center gap-2 pl-0.5">
                    <span className="flex shrink-0 items-center rounded-md p-0">{icon}</span>
                    <span className="min-w-0 truncate text-xs text-gray-900 @2xl/wrapper:text-sm">{row.label}</span>

                    <CloseCircleFilled hidden={isHkma} className={classNames("text-base text-gray-400 opacity-0 transition-opacity", {
                "opacity-100": selectedHazard
              })} />
                  </div>
                  <div className="pr-1 text-xs tabular-nums transition-all @2xl/wrapper:text-sm">
                    {normalized ? formatYAxisTickToPercent(widthPct, 2) : formatYAxisTick(metric, row.value, 4, false)}
                  </div>
                </div>
                <div className="absolute right-0 top-0 h-full w-full origin-right rounded-md transition-transform" style={{
            transform: `scaleX(${widthPct / 100})`,
            background: color + "35"
          }} />
              </ConditionalButton>;
      })}
      </div>
    </div>;
};
export default HazardSelectedYearBreakdown;